import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { Terminal } from "lucide-react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import { fetchCsrfToken } from "./lib/api";

// Lazy-load heavy pages so they only download when first visited
const Tutorials    = lazy(() => import("./pages/Tutorials"));
const Bootcamp     = lazy(() => import("./pages/Bootcamp"));
const Contact      = lazy(() => import("./pages/Contact"));
const Playground   = lazy(() => import("./pages/Playground"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AIInterview  = lazy(() => import("./pages/AIInterview"));
const About        = lazy(() => import("./pages/About"));
const Terms        = lazy(() => import("./pages/Terms"));
const Problems     = lazy(() => import("./pages/Problems"));
const ProblemDetail = lazy(() => import("./pages/ProblemDetail"));
const DailyChallenge = lazy(() => import("./pages/DailyChallenge"));

import { Loader } from "./components/Loader";

const PageLoader = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  let text = "LOADING WORKSPACE...";
  if (path.includes('/tutorials')) text = "PREPARING TUTORIAL...";
  else if (path.includes('/problems')) text = "LOADING PROBLEM...";
  else if (path.includes('/bootcamp')) text = "LOADING BOOTCAMP...";
  
  return <Loader text={text} fullScreen={true} />;
};

const MainLayout = () => (
  <>
    <Navbar />
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  </>
);

function AppRouter() {
  return (
    <Routes>
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/tutorials" element={<Tutorials />} />
        <Route path="/tutorials/:tutorialId" element={<Tutorials />} />
        <Route path="/tutorials/:tutorialId/:sectionId" element={<Tutorials />} />
        <Route path="/bootcamp" element={<Bootcamp />} />
        <Route path="/playground" element={<Playground />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/ai-interview" element={<AIInterview />} />
        <Route path="/problems" element={<Problems />} />
        <Route path="/problems/daily-challenge" element={<DailyChallenge />} />
        <Route path="/problems/:id" element={<ProblemDetail />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
      </Route>
    </Routes>
  );
}

function App() {
  // Initialize security handshake on startup
  useEffect(() => {
    fetchCsrfToken();
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <HelmetProvider>
        <div className="scanline" />
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
